import { useEffect, useMemo, useState } from 'react'
import { Bot, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react'
import {
  approveSynonym,
  deleteSynonym,
  generateSynonymsAI,
  getSynonymsAIStatus,
  listSynonyms,
  updateSynonym,
  type TenantSynonym,
  type SynonymsAIStatus,
} from '@/services/synonymsApi'
import { getProducts } from '@/lib/api/products'
import { categoriesApi, type Category } from '@/services/categoriesApi'

type Step = 1 | 2 | 3

type TargetAudienceOption = 'Jóvenes (15-25)' | 'Adultos (25-45)' | 'Mixto' | 'Empresas'

type TopProductOption = {
  id: string
  label: string
  kind: 'category' | 'product'
}

function flattenCategories(categories: Category[]): Category[] {
  const out: Category[] = []
  const visit = (node: Category) => {
    out.push(node)
    if (Array.isArray((node as any).children)) {
      for (const child of (node as any).children as Category[]) {
        visit(child)
      }
    }
  }
  for (const c of categories) visit(c)
  return out
}

function toCommaList(arr: string[]): string {
  return arr
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(', ')
}

function parseCommaList(input: string): string[] {
  return String(input || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export default function TenantSynonymsWizardPage() {
  const [step, setStep] = useState<Step>(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [status, setStatus] = useState<SynonymsAIStatus | null>(null)

  const [targetAudience, setTargetAudience] = useState<TargetAudienceOption>('Mixto')
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [topProductOptions, setTopProductOptions] = useState<TopProductOption[]>([])
  const [hasCatalogProducts, setHasCatalogProducts] = useState<boolean>(true)
  const [selectedTopProducts, setSelectedTopProducts] = useState<Set<string>>(new Set())
  const [localSlang, setLocalSlang] = useState('')

  const [rows, setRows] = useState<TenantSynonym[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingSynonyms, setEditingSynonyms] = useState<string>('')

  const pending = useMemo(() => {
    return rows.filter((r) => r.source === 'ai_generated' && r.approved === false)
  }, [rows])

  const refreshStatus = async () => {
    const s = await getSynonymsAIStatus()
    setStatus(s)
  }

  const refreshRows = async () => {
    const list = await listSynonyms({ includeUnapproved: true })
    setRows(list)
  }

  useEffect(() => {
    ;(async () => {
      try {
        setError(null)
        setLoading(true)
        await refreshStatus()
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'No se pudo cargar el estado de sinónimos.')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      try {
        setCatalogError(null)
        setCatalogLoading(true)

        const [products, categories] = await Promise.all([
          getProducts(),
          categoriesApi.getAll().catch(() => [] as Category[]),
        ])

        const normalizedProducts = Array.isArray(products) ? products : []
        setHasCatalogProducts(normalizedProducts.length > 0)

        const flatCategories = flattenCategories(Array.isArray(categories) ? categories : [])
        const categoryOpts: TopProductOption[] = flatCategories
          .filter((c: any) => Boolean(c?.name))
          .slice(0, 50)
          .map((c: any) => ({
            id: `cat:${String(c.id)}`,
            label: String(c.name),
            kind: 'category',
          }))

        const productOpts: TopProductOption[] = normalizedProducts
          .filter((p: any) => Boolean(p?.name))
          .slice(0, 80)
          .map((p: any) => ({
            id: `prod:${String(p.id)}`,
            label: String(p.name),
            kind: 'product',
          }))

        const options = [...categoryOpts, ...productOpts]
        setTopProductOptions(options)
        setSelectedTopProducts(new Set(options.slice(0, Math.min(6, options.length)).map((o) => o.id)))
      } catch (e: any) {
        setCatalogError(e?.response?.data?.error || e?.message || 'No se pudo cargar productos/categorías del catálogo.')
        setHasCatalogProducts(true)
        setTopProductOptions([])
        setSelectedTopProducts(new Set())
      } finally {
        setCatalogLoading(false)
      }
    })()
  }, [])

  const handleGenerate = async () => {
    try {
      setError(null)
      setLoading(true)

      if (!hasCatalogProducts) {
        setError('No tienes productos cargados en el catálogo. Carga productos en la base de datos para poder generar sinónimos con IA.')
        return
      }

      const topProducts = topProductOptions
        .filter((p) => selectedTopProducts.has(p.id))
        .map((p) => p.label)

      if (topProducts.length === 0) {
        setError('Selecciona al menos un grupo de productos para continuar.')
        return
      }

      await generateSynonymsAI({
        targetAudience,
        topProducts: topProducts.join(', '),
        ...(localSlang.trim() ? { localSlang: localSlang.trim() } : {}),
      })

      await refreshStatus()
      await refreshRows()
      setStep(2)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'No se pudo generar sinónimos con IA.')
    } finally {
      setLoading(false)
    }
  }

  const handleLoadPending = async () => {
    try {
      setError(null)
      setLoading(true)
      await refreshRows()
      setStep(2)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'No se pudieron cargar los sinónimos.')
    } finally {
      setLoading(false)
    }
  }

  const handleApproveOne = async (id: string) => {
    try {
      setError(null)
      setLoading(true)
      await approveSynonym(id)
      await refreshRows()
      await refreshStatus()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'No se pudo aprobar el sinónimo.')
    } finally {
      setLoading(false)
    }
  }

  const handleRejectOne = async (id: string) => {
    try {
      setError(null)
      setLoading(true)
      await deleteSynonym(id)
      await refreshRows()
      await refreshStatus()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'No se pudo rechazar el sinónimo.')
    } finally {
      setLoading(false)
    }
  }

  const handleStartEdit = (row: TenantSynonym) => {
    setEditingId(row.id)
    setEditingSynonyms(toCommaList(row.synonyms))
  }

  const handleSaveEdit = async (row: TenantSynonym) => {
    try {
      setError(null)
      setLoading(true)
      await updateSynonym(row.id, { synonyms: parseCommaList(editingSynonyms) })
      setEditingId(null)
      setEditingSynonyms('')
      await refreshRows()
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'No se pudo guardar la edición.')
    } finally {
      setLoading(false)
    }
  }

  const handleApproveAll = async () => {
    if (pending.length === 0) {
      setStep(3)
      return
    }

    try {
      setError(null)
      setLoading(true)
      for (const r of pending) {
        await approveSynonym(r.id)
      }
      await refreshRows()
      await refreshStatus()
      setStep(3)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'No se pudieron aprobar todos los sinónimos.')
    } finally {
      setLoading(false)
    }
  }

  const headerStatus = status?.status

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 sm:mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold mb-2 text-gray-900">Sinónimos con IA</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Genera sinónimos para mejorar búsquedas de productos. Los sinónimos generados por IA requieren aprobación.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Bot className="h-4 w-4 text-[#075E54]" />
          <span className="font-medium">Estado:</span>
          <span>
            {headerStatus === 'not_generated'
              ? 'No generado'
              : headerStatus === 'pending'
                ? 'Pendiente'
                : headerStatus === 'active'
                  ? 'Activo'
                  : '—'}
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="bg-white rounded-xl border shadow-sm">
        <div className="border-b px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Paso {step} de 3
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Procesando...
            </div>
          )}
        </div>

        {step === 1 && (
          <div className="p-4 sm:p-6">
            <div className="grid gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">¿Quiénes son tus clientes?</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value as TargetAudienceOption)}
                  disabled={loading}
                >
                  <option value="Jóvenes (15-25)">Jóvenes (15-25)</option>
                  <option value="Adultos (25-45)">Adultos (25-45)</option>
                  <option value="Mixto">Mixto</option>
                  <option value="Empresas">Empresas</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3">
                  <label className="block text-sm font-medium text-gray-900">Productos principales</label>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:text-blue-700"
                    onClick={() => setSelectedTopProducts(new Set(topProductOptions.map((p) => p.id)))}
                    disabled={loading || catalogLoading || topProductOptions.length === 0}
                  >
                    Seleccionar todos
                  </button>
                </div>

                {catalogError && (
                  <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {catalogError}
                  </div>
                )}

                {!hasCatalogProducts && (
                  <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    No tienes productos cargados en el catálogo. Carga productos en la base de datos para poder generar sinónimos con IA.
                  </div>
                )}

                {catalogLoading && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cargando catálogo...
                  </div>
                )}

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {topProductOptions.map((p) => {
                    const checked = selectedTopProducts.has(p.id)
                    return (
                      <label key={p.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(selectedTopProducts)
                            if (e.target.checked) next.add(p.id)
                            else next.delete(p.id)
                            setSelectedTopProducts(next)
                          }}
                          disabled={loading || catalogLoading || !hasCatalogProducts}
                        />
                        <span className="text-gray-800">{p.label}</span>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Jerga local (opcional)</label>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder='Ej: "equipo" = celular, "pana" = amigo'
                  value={localSlang}
                  onChange={(e) => setLocalSlang(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={loading || catalogLoading || !hasCatalogProducts}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#075E54] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#064c44] disabled:opacity-50"
                >
                  Generar con IA
                  <ArrowRight className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  onClick={handleLoadPending}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Revisar pendientes
                  <AlertTriangle className="h-4 w-4" />
                </button>
              </div>

              <div className="text-xs text-gray-500">
                Tip: puedes volver a generar cuando agregues productos nuevos usando el endpoint de regeneración (próximo en UI).
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-4 sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Revisión</h2>
                <p className="text-sm text-gray-600">Aprueba o ajusta los sinónimos generados por IA.</p>
              </div>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => {
                  setStep(1)
                  setError(null)
                }}
                disabled={loading}
              >
                <ArrowLeft className="h-4 w-4" />
                Volver
              </button>
            </div>

            {pending.length === 0 && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                No hay sinónimos pendientes por aprobar.
              </div>
            )}

            <div className="mt-4 grid gap-3">
              {pending.map((r) => {
                const isEditing = editingId === r.id
                return (
                  <div key={r.id} className="rounded-xl border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{r.term}</div>
                        {!isEditing && (
                          <div className="mt-1 text-sm text-gray-700">{toCommaList(r.synonyms)}</div>
                        )}
                        {isEditing && (
                          <div className="mt-2">
                            <input
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              value={editingSynonyms}
                              onChange={(e) => setEditingSynonyms(e.target.value)}
                              disabled={loading}
                            />
                            <div className="mt-1 text-xs text-gray-500">Separar sinónimos con coma.</div>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 justify-end">
                        {!isEditing && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApproveOne(r.id)}
                              disabled={loading}
                              className="rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              Aprobar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStartEdit(r)}
                              disabled={loading}
                              className="rounded-lg border px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRejectOne(r.id)}
                              disabled={loading}
                              className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              Rechazar
                            </button>
                          </>
                        )}

                        {isEditing && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleSaveEdit(r)}
                              disabled={loading}
                              className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              Guardar
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(null)
                                setEditingSynonyms('')
                              }}
                              disabled={loading}
                              className="rounded-lg border px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Cancelar
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleApproveAll}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#075E54] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#064c44] disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" />
                Aprobar todos
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Continuar
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Listo</h2>
                <p className="text-sm text-gray-600">Los sinónimos aprobados ya están activos para las búsquedas.</p>
              </div>
            </div>

            <div className="mt-4 rounded-lg border bg-gray-50 px-4 py-3 text-sm text-gray-700">
              <div><span className="font-medium">Aprobados:</span> {status?.approvedCount ?? '—'}</div>
              <div><span className="font-medium">Pendientes:</span> {status?.pendingApprovalCount ?? '—'}</div>
              <div><span className="font-medium">AI generados:</span> {status?.generatedCount ?? '—'}</div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={async () => {
                  try {
                    setLoading(true)
                    await refreshStatus()
                    await refreshRows()
                    setStep(2)
                  } finally {
                    setLoading(false)
                  }
                }}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Ver pendientes
                <AlertTriangle className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep(1)
                  setError(null)
                }}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#075E54] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#064c44] disabled:opacity-50"
              >
                Generar de nuevo
                <Bot className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
